<template>
  <v-container grid-list-xs class="pa-0">
    <v-layout row>
      <v-flex xs12>
        <v-card class="elevation-4">
          <div class="info white--text tableHeader">
            <v-card-title>
              <h2 class="headline">Test Setup</h2>
              <v-spacer></v-spacer>
            </v-card-title>
            <div class="config">
              <v-select
                label="Pool"
                v-model="selectedPool"
                :items="poolOptions"
                item-value="id"
                item-text="name"
              />
            </div>
            <PC-Dialog
              type="Field"
              :showDialog="showFieldsDialog"
              :fields="fieldFields"
              @close="closeFieldsDialog"
              @create="createField"
              @save="saveField"
            >
              <template v-slot:button>
                <v-btn
                  small
                  absolute
                  bottom
                  left
                  fab
                  color="green"
                  dark
                  @click="createField"
                >
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </template>
            </PC-Dialog>
          </div>
          <div v-if="selectedPool !== NO_SELECTION">
            <v-checkbox
              :input-value="
                itemsSetting === null || typeof itemsSetting === 'undefined'
              "
              @change="changeUseDefault"
              label="Use Default"
            />
          </div>
          <v-data-table
            ref="table"
            :headers="headers"
            :items="items"
            disable-initial-sort
            hide-actions
            :class="showTable ? '' : 'hide'"
          >
            <template slot="items" slot-scope="props">
              <td v-for="header in headers" :key="header.value">
                <v-icon v-if="header.value === 'handle'" class="handle"
                  >mdi-drag</v-icon
                >
                <v-btn
                  v-if="
                    header.value === 'actions' &&
                    props.item.id.startsWith('custom_')
                  "
                  fab
                  small
                  @click="editField(props.item.id)"
                  class="actionButton"
                >
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
                <span v-else-if="header.format === 'checkbox'">
                  <v-checkbox
                    :input-value="props.item[header.value]"
                    @change="header.change(props.item, !!$event)"
                    class="center-checkbox"
                    hide-details
                  />
                </span>
                <span v-else-if="props.item[header.value]">{{
                  props.item[header.value].name || props.item[header.value]
                }}</span>
              </td>
            </template>
          </v-data-table>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import Sortable from "sortablejs";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";

import firebase from "@/firebase";

import PCDialog from "@/components/PCDialog";

const NO_SELECTION = Symbol("no selection");

const standardItems = {
  fc: { name: "Free Chlorine (FC)" },
  ph: { name: "pH" },
  ta: { name: "Total Alkalinity (TA)" },
  ch: { name: "Calcium Hardness (CH)" },
  cya: { name: "Cyanuric Acid (CYA)" },
  borate: { name: "Borate" },
  salt: { name: "Salt (SWG Only)" },
  sug: { name: "Suggested Free Chlorine (SUG)" },
  csi: { name: "Calcite Saturation Index (CSI)" },
};

export default {
  components: { PCDialog },
  data() {
    return {
      selectedPool: NO_SELECTION,
      NO_SELECTION,
      showFieldsDialog: false,
      editID: null,
      fieldFields: [
        {
          name: "Name",
          type: "text",
          value: "",
          classes: ["pa-2", "xs12", "md6"],
        },
        {
          name: "Short Form",
          type: "text",
          value: "",
          classes: ["pa-2", "xs6", "md3"],
          maxLen: 4,
        },
        {
          name: "Type",
          type: "select-value",
          items: [
            { value: "number", name: "Number" },
            { value: "text", name: "Text" },
          ],
          classes: ["pa-2", "xs6", "md3"],
        },
        {
          name: "Range (Min)",
          type: "number",
          value: "",
          classes: ["pa-2", "xs6"],
          hide: () => this.fieldFields[2].value !== "number",
        },
        {
          name: "Range (Max)",
          type: "number",
          value: "",
          classes: ["pa-2", "xs6"],
          hide: () => this.fieldFields[2].value !== "number",
        },
        {
          name: "Goal",
          type: "number",
          value: "",
          classes: ["pa-2", "xs12"],
          hide: () => this.fieldFields[2].value !== "number",
        },
      ],
      headers: [
        {
          value: "handle",
          sortable: false,
        },
        {
          text: "Active",
          value: "active",
          format: "checkbox",
          change: this.changeActive,
        },
        { text: "Name", value: "name" },
        { text: "Actions", value: "actions" },
      ],
      items: [],
    };
  },
  computed: {
    ...mapState({
      fields: "fields",
    }),
    ...mapGetters({
      settings: "settings/getSettings",
    }),
    poolsRef() {
      return firebase.db
        .collection("users")
        .doc(firebase.auth.currentUser.uid)
        .collection("pools");
    },
    fieldsRef() {
      return firebase.db
        .collection("users")
        .doc(firebase.auth.currentUser.uid)
        .collection("fields");
    },
    poolOptions() {
      return [{ id: NO_SELECTION, name: "Default" }, ...this.settings.pools];
    },
    itemsSetting() {
      if (this.selectedPool === NO_SELECTION) {
        return this.settings.defaultTestSetup;
      }

      return this.settings.pools.find((x) => x.id === this.selectedPool)
        .testSetup;
    },
    showTable() {
      return (
        this.selectedPool === NO_SELECTION ||
        (this.itemsSetting !== null && typeof this.itemsSetting !== "undefined")
      );
    },
  },
  methods: {
    ...mapActions({
      initFields: "fields/initFields",
      initSettings: "settings/initSettings",
      saveSettings: "settings/saveSettings",
    }),
    ...mapMutations({
      addField: "fields/addField",
      setSettings: "settings/setSettings",
      updateField: "fields/updateField",
      updatePool: "settings/updatePool",
    }),
    changeActive(item, newValue) {
      console.log("changeActive", item, newValue, this.selectedPool);

      item.active = newValue;

      this.saveSetup();
    },
    saveSetup() {
      const newSetup = this.items.map((x) => ({
        id: x.id,
        active: x.active,
      }));
      if (this.selectedPool === NO_SELECTION) {
        this.setSettings({ defaultTestSetup: newSetup });
        this.saveSettings(this.settings);
      } else {
        this.poolsRef
          .doc(this.selectedPool)
          .update({ testSetup: newSetup })
          .then(() => {
            this.updatePool({
              id: this.selectedPool,
              changes: { testSetup: newSetup },
            });
          });
      }
    },
    changeUseDefault(newValue) {
      if (newValue) {
        this.poolsRef
          .doc(this.selectedPool)
          .update({ testSetup: null })
          .then(() => {
            this.updatePool({
              id: this.selectedPool,
              changes: { testSetup: null },
            });
          });
      } else {
        this.saveSetup();
      }
    },
    closeFieldsDialog() {
      this.fieldFields.forEach((field) => (field.value = ""));
      this.showFieldsDialog = false;
    },
    createField() {
      this.editID = null;
      this.showFieldsDialog = true;
    },
    editField(id) {
      if (!id.startsWith("custom_")) return;

      this.editID = id.substring(7);

      const field = this.fields.find((x) => x.id === this.editID);

      this.fieldFields[0].value = field.name;
      this.fieldFields[1].value = field.shortForm;
      this.fieldFields[2].value = field.type;
      if (field.type === "number") {
        this.fieldFields[3].value =
          typeof field.range === "undefined" || field.range.min === null
            ? ""
            : field.range.min;
        this.fieldFields[4].value =
          typeof field.range === "undefined" || field.range.max === null
            ? ""
            : field.range.max;

        this.fieldFields[5].value =
          typeof field.goal === "undefined" || field.goal === null
            ? ""
            : field.goal;
      }

      this.fieldFields = [...this.fieldFields];

      this.showFieldsDialog = true;
    },
    async saveField() {
      const info = {
        name: this.fieldFields[0].value,
        shortForm: this.fieldFields[1].value,
        type: this.fieldFields[2].value,
      };

      if (info.type === "number") {
        info.range = {
          min:
            this.fieldFields[3].value === ""
              ? null
              : Number(this.fieldFields[3].value),
          max:
            this.fieldFields[4].value === ""
              ? null
              : Number(this.fieldFields[4].value),
        };

        info.goal =
          this.fieldFields[5].value === ""
            ? null
            : Number(this.fieldFields[5].value);
      }

      if (this.editID === null) {
        const doc = await this.fieldsRef.add(info);
        this.addField({ ...info, id: doc.id });
      } else {
        await this.fieldsRef.doc(this.editID).set(info);
        this.updateField({ ...info, id: this.editID });
      }

      this.fields.forEach((field) => (field.value = ""));
      this.showFieldsDialog = false;
    },
  },
  watch: {
    itemsSetting: {
      handler: updateItems,
      immediate: true,
    },
    fields: {
      handler: updateItems,
    },
    settings: {
      deep: true,
      handler() {
        console.log("settings deep");
      },
    },
    "settings.defaultTestSetup": {
      handler() {
        console.log("dsnehdnehstne");
      },
    },
  },
  async mounted() {
    await Promise.all([this.initSettings(), this.initFields()]);

    console.log(this.$refs);

    Sortable.create(this.$refs.table.$el.querySelector("tbody"), {
      handle: ".handle",
      onEnd: ({ newIndex, oldIndex }) => {
        const moved = this.items.splice(oldIndex, 1)[0];
        this.items.splice(newIndex, 0, moved);

        this.saveSetup();
      },
    });
  },
};

function updateItems() {
  let result = this.itemsSetting;

  console.log("items are", result);

  if (result === null || typeof result === "undefined") {
    result = Object.keys(standardItems).map((key) => ({
      id: key,
      active: true,
    }));
  }

  const known = new Set();

  result = result
    .map((entry) => {
      if (entry.id in standardItems) {
        known.add(entry.id);
        return { ...entry, name: standardItems[entry.id].name };
      }
      if (entry.id.startsWith("custom_")) {
        const found = this.fields.find((x) => x.id === entry.id.substring(7));
        if (typeof found !== "undefined") {
          known.add(entry.id);
          return {
            ...entry,
            name:
              found.name +
              (found.shortForm ? " (" + found.shortForm + ")" : ""),
          };
        }
      }
      return null;
    })
    .filter((x) => x !== null);

  for (const key in standardItems) {
    if (!known.has(key)) {
      result.push({ id: key, active: false, name: standardItems[key].name });
    }
  }

  console.log("updating items, fields are", this.fields);
  this.fields.forEach((field) => {
    if (!known.has("custom_" + field.id)) {
      result.push({
        id: "custom_" + field.id,
        active: false,
        name:
          field.name + (field.shortForm ? " (" + field.shortForm + ")" : ""),
      });
    }
  });

  this.items = result;
}
</script>

<style lang="scss" scoped>
.config {
  padding-left: 16px;
  padding-right: 16px;
}
.tableHeader {
  position: relative;
}
.actionButton {
  width: 20px;
  height: 20px;
}
.handle {
  cursor: grab;
}
</style>
